<template>
  <FusionBaseDialog
    @close="close"
    :open="open"
    :title="$t('map.mapSettings.title')"
    icon="mdi-cog"
    :max-width="320"
    :persistent="true"
    :content-class="contentClass"
    data-cy="mapSettingsHeader"
  >
    <div class="card-content" data-cy="mapSettingsDialog">
      <!-- empty element is bandaid for blur bug on first element in dialog -->
      <v-switch style="display: none"></v-switch>
      <v-select
        v-model="aqiType"
        :items="aqiTypeItems"
        :label="$t('map.mapSettings.aqiType')"
        data-cy="mapSettingsAqiTypeSelect"
      ></v-select>
      <v-select
        v-model="measurementType"
        :items="measurementTypeItems"
        :label="$t('map.mapSettings.measurementType')"
        data-cy="mapSettingsMeasurementTypeSelect"
      ></v-select>
      <v-select
        v-model="visibleDeviceModels"
        class="mb-4"
        :items="deviceModelItems"
        multiple
        :label="$t('map.mapSettings.deviceModels.label')"
        :placeholder="$t('map.mapSettings.deviceModels.placeholder')"
        :hint="$t('map.mapSettings.deviceModels.hint')"
        persistent-hint
        data-cy="mapSettingsDeviceSelect"
      ></v-select>
      <v-switch
        color="primary"
        hide-details
        :label="$t('map.mapSettings.showMine')"
        v-model="showMine"
        data-cy="mapSettingsShowMySensors"
      ></v-switch>
      <v-switch
        color="primary"
        hide-details
        :label="$t('map.mapSettings.showShared')"
        v-model="showShared"
        data-cy="mapSettingsShowSharedSensors"
      ></v-switch>
      <v-switch
        color="primary"
        hide-details
        :label="$t('map.mapSettings.showPublic')"
        v-model="showPublic"
        data-cy="mapSettingsShowPublicSensors"
      ></v-switch>
      <div class="secondary-indicator">
        <v-switch
          color="primary"
          hide-details
          :label="$t('map.mapSettings.showOutdoor')"
          v-model="showOutdoor"
          data-cy="mapSettingsShowOutdoor"
        ></v-switch>
        <div class="device-type-indicator">
          <img src="img/map/pin-blank-outdoor.png" alt="Outdoor Device Pin" />
        </div>
      </div>
      <div class="secondary-indicator">
        <v-switch
          color="primary"
          hide-details
          :label="$t('map.mapSettings.showIndoor')"
          v-model="showIndoor"
          data-cy="mapSettingsShowIndoor"
        ></v-switch>
        <div class="device-type-indicator">
          <img src="img/map/pin-blank-indoor.png" class="indoor-type" alt="Indoor Device Pin" />
        </div>
      </div>
    </div>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn @click="close" class="bg-primary text-white" variant="text" data-cy="mapSettingsClose">
        {{ $t('buttons.done') }}
      </v-btn>
    </template>
  </FusionBaseDialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useDisplay } from 'vuetify'
import { measurementTypes, aqiTypes } from '@/store/map/utils'
import { NAMESPACES } from '@/store/types'
import { MAP_GETTERS, MAP_ACTIONS } from '@/store/map'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'

interface SelectItem {
  title: string
  value: number | string
}

export default defineComponent({
  name: 'MapSettingsDialog',
  props: {
    open: { type: Boolean, required: true }
  },
  emits: ['close'],
  setup() {
    const { xs, mdAndUp } = useDisplay()
    return { xs, mdAndUp }
  },
  data() {
    return {
      measurementTypeItems: [
        {
          title: this.$t('map.mapSettings.measurementOptions.aqiPm25'),
          value: measurementTypes.pm25
        },
        {
          title: this.$t('map.mapSettings.measurementOptions.aqiPm10'),
          value: measurementTypes.pm10
        }
      ] as SelectItem[],
      aqiTypeItems: [
        {
          title: this.$t('map.mapSettings.aqiTypeOptions.us'),
          value: aqiTypes.us
        },
        {
          title: this.$t('map.mapSettings.aqiTypeOptions.in'),
          value: aqiTypes.in
        }
      ] as SelectItem[]
    }
  },
  computed: {
    measurementType: {
      get(): number {
        return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
      },
      set(value: number): void {
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          pinMeasurementType: value
        })
      }
    },
    aqiType: {
      get(): number {
        return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_AQI_TYPE}`]
      },
      set(value: number): void {
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          pinAqiType: value
        })
      }
    },
    deviceModels: {
      get(): string[] | null {
        return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_DEVICE_MODELS}`]
      },
      set(value: string[]): void {
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          deviceModels: value
        })
      }
    },
    showMine: {
      get(): boolean {
        return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_SHOW_MINE}`]
      },
      set(value: boolean): void {
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          showMine: value
        })
      }
    },
    showPublic: {
      get(): boolean {
        return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_SHOW_PUBLIC}`]
      },
      set(value: boolean): void {
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          showPublic: value
        })
      }
    },
    showShared: {
      get(): boolean {
        return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_SHOW_SHARED}`]
      },
      set(value: boolean): void {
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          showShared: value
        })
      }
    },
    showOutdoor: {
      get(): boolean {
        return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_SHOW_OUTDOOR}`]
      },
      set(value: boolean): void {
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          showOutdoor: value
        })
      }
    },
    showIndoor: {
      get(): boolean {
        return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_SHOW_INDOOR}`]
      },
      set(value: boolean): void {
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          showIndoor: value
        })
      }
    },
    hiddenDeviceModels(): string[] {
      const storeModels = this.deviceModels
      if (!storeModels) return []
      const visibleValues = this.deviceModelItems.map((item) => item.value)
      return storeModels.filter((model) => !visibleValues.includes(model))
    },
    visibleDeviceModels: {
      get(): string[] {
        const storeModels = this.deviceModels
        if (!storeModels) return []
        const visibleValues = this.deviceModelItems.map((item) => item.value)
        return storeModels.filter((model) => visibleValues.includes(model))
      },
      set(value: string[]): void {
        const merged = [...value, ...this.hiddenDeviceModels]
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          deviceModels: merged.length ? merged : null
        })
      }
    },
    deviceModelItems(): SelectItem[] {
      const models: string[] =
        this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_MAP_SETTINGS_MODELS}`
        ]
      return models.map((modelNumber: string) => {
        const modelKey: string =
          this.$store.getters[`${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`](
            modelNumber
          )
        return {
          title: this.$t(`map.mapSettings.deviceModelNames.${modelKey}`),
          value: modelNumber
        }
      })
    },
    contentClass(): string {
      const classNames = []
      if (!this.xs) classNames.push('map-settings')
      if (this.mdAndUp) classNames.push('mdAndUp')
      return classNames.join(' ')
    }
  },
  methods: {
    close(): void {
      this.$emit('close')
    }
  }
})
</script>

<style lang="scss">
.map-settings {
  --app-bar-height: 56px;
  --edge-inset: 32px;

  margin: 0 !important; // Remove the default dialog margin

  position: absolute;
  right: var(--edge-inset);
  top: calc(var(--app-bar-height) + var(--edge-inset));
  max-height: calc(100% - (var(--app-bar-height) + var(--edge-inset) * 2)) !important;

  &.mdAndUp {
    --app-bar-height: 64px;
  }
}
</style>

<style lang="scss" scoped>
.v-dialog {
  .v-card {
    padding: 16px;
  }
  .v-card-title {
    font-weight: 300;
    padding: 4px 24px 24px 5px;
  }
  .v-card-subtitle {
    padding: 8px 16px;
  }
  .top-subtitle {
    padding: 20px 16px 4px 24px;
    font-size: 16px;
  }
  .secondary-indicator {
    display: flex;
    .device-type-indicator {
      align-self: center;
      margin: 0 0 8px 16px;
      img {
        height: 36px;
      }
    }
    .indoor-type {
      margin-left: 11px;
    }
  }
  .v-btn {
    min-width: 120px !important;
    @media (min-width: 496px) {
      min-width: 160px !important;
    }
  }
  .v-input--switch {
    margin-top: 8px;
  }
  .text-dark {
    color: black;
  }
}

.card-content {
  padding: 0.4rem;
}
</style>
